import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import TimeSlotPicker, {
  PrebookSlot,
} from '@ikea-customer-self-serve/ts-picker';
import useLocaleContext from '../hooks/useLocale';
import { layout } from '../styles';
import {
  getReplacementServiceStoreName,
  getReplacementServiceTitle,
  removeTimePartFromDate,
  ServiceLocation,
  startOfWeekDayToNumber,
} from '../helpers';
import { GetAvailableTimeslotsArgs } from '../hooks/getAvailableTimeslots';
import analyticsApiClient from '../services/Analytics';
import { mediaQueries } from '../styles/mediaQueries';
import { Slots } from '../services/Appointment/model';
import { useStateDispatch, useStateSelector } from '../store';
import {
  clearSelectedTimeSlot,
  updateSelectedTimeSlot,
} from '../reducers/app/appReducer';
import { timeZoneHelper } from '../helpers/timeZoneHelper';
import useTranslation from '../hooks/useTranslation';
import NoTimeSlotsMessage from './NoTimeSlotsMessage';
import { Trans } from 'react-i18next';
import { selectSelectedOriginalService } from '../reducers/stores/selectors';
import { getFirstAvailableTimeSlot } from '../helpers/timeSlotHelper';
export interface TimePickerProps {
  weeks: number;
  slots: Slots[];
  replacementServiceSlots: Slots[];
  prebooked: {
    date: string;
    id: string;
  };
  serviceId: string;
  zipCode: string;
  onTimespanChange: (args: GetAvailableTimeslotsArgs) => void;
  prevEnabled: boolean;
  from: string;
  location: ServiceLocation;
  isInStore: boolean;
  operation?: string;
  disabled?: boolean;
}

const StyledTimeSlotPicker = styled(TimeSlotPicker)`
  margin-top: ${layout.contentMarginTop.mobile};
  ${mediaQueries.portraitTabletUp} {
    margin-top: ${layout.contentMarginTop.desktop};
  }
`;

/* global CSS_CONFIG */
const { dateAndTime, firstDayOfWeek } = CSS_CONFIG.FORMATS;
const shortDate = dateAndTime.date;
const { longDate } = dateAndTime;

const TimePicker: React.FunctionComponent<TimePickerProps> = ({
  weeks,
  slots,
  replacementServiceSlots,
  prevEnabled,
  serviceId,
  onTimespanChange,
  from,
  prebooked,
  location,
  isInStore,
  operation,
  disabled,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { locale } = useLocaleContext();
  const { handleEventInGoogleAnalytic } = analyticsApiClient();
  const dispatch = useStateDispatch();

  const originalService = useStateSelector(selectSelectedOriginalService);
  const selectedReplacementServiceTimeSlot = useStateSelector(
    (state) => state.app.selected.replacementService?.timeSlot,
  );
  const replacementServiceStore = useStateSelector(
    (state) => state.app.selected.replacementService?.store,
  );
  const { replacementService } = originalService || {};

  const [
    showReplacementServiceOnNoTimeSlotsError,
    setShowReplacementServiceOnNoTimeSlotsError,
  ] = useState(false);

  const preBookedDate = isInStore
    ? removeTimePartFromDate(prebooked.date)
    : prebooked.date;

  const preselectedSlot: PrebookSlot = React.useMemo(
    (): PrebookSlot => ({
      date: preBookedDate,
      timeSlotId: prebooked.id,
    }),
    [preBookedDate, prebooked.id],
  );

  const handleOnChange = ({
    id,
    dateChanged,
    isReplacementService,
  }: {
    id: string;
    dateChanged: boolean;
    isReplacementService: boolean;
  }): void => {
    const serviceProductId = isReplacementService
      ? replacementService?.serviceId
      : serviceId;
    const serviceOperation = isReplacementService
      ? replacementService?.operation
      : operation;
    if (dateChanged) {
      handleEventInGoogleAnalytic(
        `switching_date_${serviceProductId}_${serviceOperation}_appointment`,
      );
    } else {
      handleEventInGoogleAnalytic(
        `switching_time_${serviceProductId}_${serviceOperation}_appointment`,
      );
    }
    dispatch(
      updateSelectedTimeSlot({
        slots: isReplacementService ? replacementServiceSlots : slots,
        id,
        isReplacementTimeSlot: isReplacementService,
      }),
    );
  };

  useEffect(() => {
    if (preselectedSlot.timeSlotId) {
      dispatch(
        updateSelectedTimeSlot({
          slots,
          id: preselectedSlot.timeSlotId,
          isReplacementTimeSlot: false,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimespanChange = (date: string): void => {
    dispatch(clearSelectedTimeSlot());
    onTimespanChange([date]);
  };

  const handleReplacementServiceButtonClicked = (): void => {
    const replacementServiceFirstSlot = getFirstAvailableTimeSlot(
      replacementServiceSlots,
    );
    dispatch(
      updateSelectedTimeSlot({
        slots: replacementServiceSlots,
        id: replacementServiceFirstSlot?.id || '',
        isReplacementTimeSlot: true,
      }),
    );
    setShowReplacementServiceOnNoTimeSlotsError(true);
  };

  const selectTimeSlotText = `${t('components.timePicker.subTitle', {
    serviceName: t(`services.name.${serviceId}`),
  })}`;
  const replacementServiceTitle: string =
    replacementServiceSlots.length !== 0
      ? t(`services.name.${replacementService?.serviceId}`)
      : '';
  const noTimeslotMessage =
    slots.length === 0 && !showReplacementServiceOnNoTimeSlotsError ? (
      <NoTimeSlotsMessage
        replacementServiceSlots={replacementServiceSlots}
        serviceId={serviceId}
        replacementServiceTitle={replacementServiceTitle}
        onReplacementServiceButtonClicked={
          handleReplacementServiceButtonClicked
        }
      />
    ) : null;

  return (
    <div data-cy="time-slot-picker">
      <StyledTimeSlotPicker
        ariaTodayWord={t('pages.timeslots.ariaTodayWord')}
        slots={slots}
        numberOfWeeks={weeks}
        title={t('pages.timeslots.timePickerTitle')}
        timeSlotHeaderText={selectTimeSlotText}
        noAvailableTimeWindowsError={t(
          'components.timePicker.noAvailableTimeWindowsError',
        )}
        storeName={
          replacementService
            ? getReplacementServiceStoreName(
                replacementService.location,
                replacementServiceStore?.text || '',
                t,
              )
            : undefined
        }
        replacementServiceSwitchLabel={
          replacementService ? (
            <Trans
              i18n={i18n}
              i18nKey={'components.timePicker.replacementServiceSwitchLabel'}
              components={{
                replacementServiceSwitchLabel: (
                  <strong>
                    {getReplacementServiceTitle(
                      replacementService.location,
                      replacementServiceTitle,
                      t,
                    )}
                  </strong>
                ),
              }}
            />
          ) : undefined
        }
        replacementServiceSwitchHeading={t(
          'components.timePicker.replacementServiceSwitchHeading',
        )}
        shouldStartOn={from}
        timeSlotsMessage={noTimeslotMessage}
        originalServiceTimeZoneTextInfo={
          timeZoneHelper(location, t, CSS_CONFIG).timeZoneTextInfo
        }
        replacementServiceTimeZoneTextInfo={
          replacementService
            ? timeZoneHelper(replacementService.location, t, CSS_CONFIG)
                .timeZoneTextInfo
            : undefined
        }
        preselectedSlot={preselectedSlot}
        replacementServiceSelectedTimeSlot={
          selectedReplacementServiceTimeSlot || undefined
        }
        replacementServiceSlots={
          slots.length === 0 &&
          replacementServiceSlots.length !== 0 &&
          !showReplacementServiceOnNoTimeSlotsError
            ? []
            : replacementServiceSlots
        }
        onChange={handleOnChange}
        onNextClick={handleTimespanChange}
        onPrevClick={handleTimespanChange}
        nextText={t('pages.timeslots.ariaNextText')}
        previousText={t('pages.timeslots.ariaPrevText')}
        prevEnabled={prevEnabled as boolean}
        shortDateFormat={shortDate}
        longDateFormat={longDate}
        locale={locale}
        weekStartsOn={startOfWeekDayToNumber(firstDayOfWeek)}
        disabled={disabled}
      />
    </div>
  );
};

export default TimePicker;
